import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
    Newspaper, Trophy, Target, ChevronDown, ChevronUp,
    Loader2, RefreshCw, Clock, Star, Globe, Users,
    ArrowRight, X, Calendar, ChevronLeft, ChevronRight, Heart,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../components/ui/command';
import { format, parseISO, isToday, isPast, isFuture, startOfDay } from 'date-fns';
import axios from 'axios';
import { getToken } from '../lib/auth';
import { badgeUrl } from '../lib/assetUrls';
import { usePlayerPhoto } from '../lib/usePlayerPhoto';
import { isWcTeam, teamAbbr } from '../lib/wcTeams';
import { TeamIntelTooltip } from '../components/TeamIntelTooltip';
import { useAuth } from '../context/AuthContext';
import { PlayerProfileModal } from '../components/PlayerProfileModal';
import BracketMatchModal from '../components/BracketMatchModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Helpers ───────────────────────────────────────────────────────────────────

function authHeaders() {
    return { Authorization: `Bearer ${getToken()}` };
}

function extractArticleId(item) {
    if (item.uri) return item.uri;
    try {
        const href = item.links?.[0]?.href || '';
        const segs = href.split('/');
        return segs[segs.length - 1].split('?')[0];
    } catch { return null; }
}

function itemImage(item, rendition = '4x3') {
    return item?.associations?.featureimage?.renditions?.[rendition]?.href || null;
}

function fmtIssued(iso) {
    if (!iso) return '';
    try {
        return format(parseISO(iso), 'd MMM yyyy, HH:mm');
    } catch { return iso; }
}

function fmtMatchDate(yyyymmdd) {
    if (!yyyymmdd || yyyymmdd.length < 8) return yyyymmdd;
    try {
        const d = parseISO(`${yyyymmdd.slice(0, 4)}-${yyyymmdd.slice(4, 6)}-${yyyymmdd.slice(6, 8)}`);
        if (isToday(d)) return 'Today';
        return format(d, 'EEE d MMM');
    } catch { return yyyymmdd; }
}

function fmtKickoff(iso) {
    if (!iso) return '';
    try { return format(parseISO(iso), 'HH:mm'); } catch { return ''; }
}

// Season names come back as "<League Name> 25/26" — strip the redundant
// league-name prefix so the tag next to the league name just reads "25/26".
function shortSeasonLabel(seasonName, compName) {
    if (!seasonName) return '';
    if (compName && seasonName.toLowerCase().startsWith(compName.toLowerCase())) {
        return seasonName.slice(compName.length).trim();
    }
    return seasonName;
}

// ── Social embed processing ───────────────────────────────────────────────────

function processArticleBody(html) {
    if (!html) return [];

    // Decode common JSON escape sequences PA sometimes leaves in
    let h = html
        .replace(/\\u003C/g, '<').replace(/\\u003E/g, '>')
        .replace(/\\u0026/g, '&').replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"');

    // Strip Kaltura video embeds — PA's player SDK isn't available to us,
    // so remove the player div and its aspect-ratio wrapper to avoid blank boxes.
    h = h.replace(/<div[^>]*\bid="kaltura_player_[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '');
    h = h.replace(/<div[^>]*style="[^"]*padding-bottom\s*:\s*\d+(?:\.\d+)?%[^"]*"[^>]*>\s*<\/div>/gi, '');

    // Strip StreamAMG video embeds — PA's streaming player, renders as blank space on our domain.
    h = h.replace(/<div[^>]*style="[^"]*display\s*:\s*inline-block[^"]*"[^>]*>[\s\S]*?<\/div>\s*<script[^>]*streamamg[^>]*><\/script>/gi, '');
    h = h.replace(/<script[^>]*streamamg[^>]*>[\s\S]*?<\/script>/gi, '');

    // Strip PA's injected Google Analytics tracking blocks — they fire on our domain
    // and leak PA internal dimension data; silently remove them.
    h = h.replace(/<!--\s*TRACKING START GA\s*-->[\s\S]*?<!--\s*TRACKING END GA\s*-->/gi, '');

    // Extract Flourish embeds before segment splitting so the script tag doesn't get swallowed
    h = h.replace(/<div[^>]*class="[^"]*flourish-embed[^"]*"[^>]*data-src="([^"]+)"[^>]*>[\s\S]*?<\/div>/gi,
        (_, dataSrc) => `<!--FLOURISH:${dataSrc}-->`);

    const segments = [];
    const combinedRe = /<!--\s*EMBED START (Twitter|X|Instagram)\s*-->([\s\S]*?)<!--\s*EMBED END \1\s*-->|<!--FLOURISH:([^>]+?)-->/gi;
    let lastIndex = 0;
    let match;

    while ((match = combinedRe.exec(h)) !== null) {
        if (match.index > lastIndex) {
            segments.push({ type: 'html', content: h.slice(lastIndex, match.index) });
        }
        if (match[3] !== undefined) {
            // Flourish placeholder
            segments.push({ type: 'flourish', dataSrc: match[3] });
        } else {
            const platform = match[1].toLowerCase();
            if (platform === 'twitter' || platform === 'x') {
                segments.push({ type: 'tweet', ...parseTweet(match[2]) });
            } else {
                const urlM = match[2].match(/href="([^"]*instagram\.com[^"]*)"/i);
                segments.push({ type: 'instagram', url: urlM?.[1] || '' });
            }
        }
        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < h.length) segments.push({ type: 'html', content: h.slice(lastIndex) });
    return segments.length ? segments : [{ type: 'html', content: h }];
}

function parseTweet(embedHtml) {
    const bqM = embedHtml.match(/<blockquote[^>]*>([\s\S]*?)<\/blockquote>/i);
    if (!bqM) return { text: '', author: '', username: '', date: '', url: '' };
    const inner = bqM[1];

    const paras = [];
    const pRe = /<p[^>]*>([\s\S]*?)<\/p>/gi;
    let m;
    while ((m = pRe.exec(inner)) !== null) paras.push(m[1]);

    const lastPara = paras[paras.length - 1] || '';
    const decodeEntities = (str) => {
        const txt = document.createElement('textarea');
        txt.innerHTML = str;
        return txt.value;
    };
    const tweetText = decodeEntities(paras.slice(0, -1).map(p => p.replace(/<[^>]+>/g, '')).join(' ').trim());

    const urlM = lastPara.match(/href="(https?:\/\/(?:twitter|x)\.com\/[^/]+\/status\/(\d+)[^"]*)">([^<]+)<\/a>/i);
    const url = urlM?.[1] || '';
    const date = urlM?.[3] || '';

    const usernameM = lastPara.match(/@(\w+)/);
    const username = usernameM?.[1] || '';
    const nameM = lastPara.match(/—\s*([^(@<\n]+)/);
    const author = nameM?.[1]?.trim() || username;

    return { text: tweetText, author, username, date, url };
}

function FlourishEmbed({ dataSrc }) {
    const ref = useRef(null);
    useEffect(() => {
        if (!dataSrc || !ref.current) return;
        if (window.Flourish) { window.Flourish.reload?.(); return; }
        if (!document.getElementById('flourish-embed-js')) {
            const s = document.createElement('script');
            s.id = 'flourish-embed-js';
            s.src = 'https://public.flourish.studio/resources/embed.js';
            s.async = true;
            document.head.appendChild(s);
        }
    }, [dataSrc]);
    if (!dataSrc) return null;
    return (
        <div ref={ref} className="flourish-embed" data-src={dataSrc} style={{ width: '100%', minHeight: 400 }} />
    );
}

function TweetCard({ url, text }) {
    const ref = useRef(null);

    useEffect(() => {
        if (!url || !ref.current) return;
        const load = () => window.twttr?.widgets?.load(ref.current);
        if (window.twttr?.widgets) {
            load();
        } else {
            // Inject widgets.js once if not already present
            if (!document.getElementById('twitter-widgets-js')) {
                const s = document.createElement('script');
                s.id = 'twitter-widgets-js';
                s.src = 'https://platform.twitter.com/widgets.js';
                s.async = true;
                s.charset = 'utf-8';
                s.onload = load;
                document.head.appendChild(s);
            } else {
                // Script tag exists but twttr not ready yet — poll briefly
                const t = setInterval(() => {
                    if (window.twttr?.widgets) { clearInterval(t); load(); }
                }, 100);
                setTimeout(() => clearInterval(t), 5000);
            }
        }
    }, [url]);

    if (!url) return null;

    return (
        <div ref={ref} className="my-3 max-w-lg mx-auto flex justify-center">
            <blockquote className="twitter-tweet" data-theme="dark" data-dnt="true">
                <a href={url}>{text || 'View tweet'}</a>
            </blockquote>
        </div>
    );
}

function InstagramCard({ url }) {
    const ref = useRef(null);
    useEffect(() => {
        if (!url || !ref.current) return;
        const load = () => window.instgrm?.Embeds?.process();
        if (window.instgrm?.Embeds) { load(); return; }
        if (!document.getElementById('instagram-embed-js')) {
            const s = document.createElement('script');
            s.id = 'instagram-embed-js';
            s.src = 'https://www.instagram.com/embed.js';
            s.async = true; s.onload = load;
            document.head.appendChild(s);
        } else {
            const t = setInterval(() => { if (window.instgrm?.Embeds) { clearInterval(t); load(); } }, 100);
            setTimeout(() => clearInterval(t), 5000);
        }
    }, [url]);
    if (!url) return null;
    const embedUrl = url.split('?')[0].replace(/\/?$/, '/');
    return (
        <div ref={ref} className="my-3 max-w-lg mx-auto flex justify-center">
            <blockquote
                className="instagram-media"
                data-instgrm-permalink={embedUrl}
                data-instgrm-version="14"
                data-instgrm-captioned
                style={{ background: '#FFF', border: 0, borderRadius: 3, minWidth: 326, width: 'calc(100% - 2px)' }}
            >
                <a href={embedUrl}>View on Instagram</a>
            </blockquote>
        </div>
    );
}

function ArticleBody({ html }) {
    const segments = useMemo(() => processArticleBody(html), [html]);
    return (
        <div>
            {segments.map((seg, i) => {
                if (seg.type === 'tweet') return <TweetCard key={i} {...seg} />;
                if (seg.type === 'instagram') return <InstagramCard key={i} url={seg.url} />;
                if (seg.type === 'flourish') return <FlourishEmbed key={i} dataSrc={seg.dataSrc} />;
                return (
                    <div
                        key={i}
                        className="prose prose-sm dark:prose-invert max-w-none text-muted-foreground leading-relaxed article-body"
                        dangerouslySetInnerHTML={{ __html: seg.content }}
                    />
                );
            })}
        </div>
    );
}

// ── Article Modal ─────────────────────────────────────────────────────────────

function ArticleModal({ articleId, onClose }) {
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!articleId) return;
        setLoading(true);
        setArticle(null);
        axios.get(`${API_URL}/api/football/hub/article`, {
            params: { item_id: articleId },
            headers: authHeaders(),
        }).then(r => setArticle(r.data)).catch(() => setArticle(null)).finally(() => setLoading(false));
    }, [articleId]);

    const imageUrl = article?.associations?.featureimage?.renditions?.['16x9']?.href
        || article?.associations?.featureimage?.renditions?.['4x3']?.href || null;

    const tags = (article?.subject || [])
        .filter(s => ['sport', 'tag', 'competition'].includes(s.profile))
        .slice(0, 5);

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
                <DialogHeader className="sticky top-0 z-10 bg-background border-b px-6 py-4">
                    <DialogTitle className="pr-8 text-base font-bold leading-snug uppercase">
                        {loading ? <Skeleton className="h-5 w-3/4" /> : article?.headline}
                    </DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="p-6 space-y-3">
                        <Skeleton className="h-48 w-full" />
                        <Skeleton className="h-4 w-1/3" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-2/3" />
                    </div>
                ) : !article || article.error ? (
                    <div className="p-6 text-center text-muted-foreground">Could not load article.</div>
                ) : (
                    <div className="p-6 space-y-4">
                        {imageUrl && (
                            <img src={imageUrl} alt={article.headline} className="w-full rounded-lg object-cover max-h-72" />
                        )}
                        <div className="border-b pb-3">
                            {article.byline && <p className="text-sm italic text-muted-foreground">{article.byline}</p>}
                            {article.issued && <p className="text-xs text-muted-foreground mt-0.5">{fmtIssued(article.issued)}</p>}
                        </div>
                        {article.description_text && (
                            <p className="font-semibold text-foreground leading-snug">{article.description_text}</p>
                        )}
                        {(article.body_html || article.body_text) && (
                            <ArticleBody html={article.body_html || `<p>${article.body_text}</p>`} />
                        )}
                        {tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 pt-2 border-t">
                                {tags.map(t => (
                                    <Badge key={t.qcode || t.name} variant="secondary" className="text-xs">{t.name}</Badge>
                                ))}
                            </div>
                        )}
                        <div className="pt-2 border-t">
                            <Button variant="outline" className="w-full" onClick={onClose}>
                                Close
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

// ── News Section ──────────────────────────────────────────────────────────────

function NewsSection({ items, loading, onArticleClick }) {
    if (loading) {
        return (
            <div className="grid md:grid-cols-3 gap-4">
                <Skeleton className="h-80 md:col-span-1" />
                <div className="md:col-span-2 space-y-4">
                    {[1, 2, 3].map(i => <Skeleton key={i} className="h-24" />)}
                </div>
            </div>
        );
    }

    if (!items?.length) {
        return (
            <Card>
                <CardContent className="py-10 text-center text-muted-foreground">
                    <Newspaper className="h-7 w-7 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">News coming soon — configure PA_CONTENT_API_KEY to enable.</p>
                </CardContent>
            </Card>
        );
    }

    const [hero, ...rest] = items;
    const secondary = rest.slice(0, 3);
    const grid = rest.slice(3, 7);
    const heroImg = itemImage(hero, '4x3');
    const heroId = extractArticleId(hero);

    return (
        <div className="space-y-4">
            {/* Hero + secondary */}
            <div className="grid md:grid-cols-5 gap-4">
                {/* Hero story */}
                <button
                    onClick={() => heroId && onArticleClick(heroId)}
                    className="md:col-span-2 text-left group rounded-xl overflow-hidden border bg-card hover:border-primary/50 transition-colors"
                >
                    {heroImg ? (
                        <div className="aspect-[4/3] overflow-hidden">
                            <img src={heroImg} alt={hero.headline} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        </div>
                    ) : (
                        <div className="aspect-[4/3] bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                            <Globe className="h-12 w-12 text-primary/30" />
                        </div>
                    )}
                    <div className="p-4">
                        <Badge className="mb-2 text-xs bg-red-500 hover:bg-red-500 text-white">Football</Badge>
                        <h2 className="font-bold text-base leading-snug group-hover:text-primary transition-colors line-clamp-3">{hero.headline}</h2>
                        <p className="text-xs text-muted-foreground mt-2">{fmtIssued(hero.issued)}</p>
                    </div>
                </button>

                {/* Secondary stories */}
                <div className="md:col-span-3 flex flex-col gap-3">
                    {secondary.map(item => {
                        const img = itemImage(item, '4x3');
                        const id = extractArticleId(item);
                        return (
                            <button
                                key={id || item.headline}
                                onClick={() => id && onArticleClick(id)}
                                className="flex gap-3 text-left group rounded-lg border bg-card p-3 hover:border-primary/50 transition-colors"
                            >
                                {img ? (
                                    <img src={img} alt={item.headline} className="w-24 h-18 object-cover rounded flex-shrink-0" style={{ height: '72px', width: '96px' }} />
                                ) : (
                                    <div className="w-24 h-18 bg-muted rounded flex-shrink-0 flex items-center justify-center" style={{ height: '72px', width: '96px' }}>
                                        <Globe className="h-5 w-5 text-muted-foreground" />
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <Badge className="mb-1 text-xs bg-red-500 hover:bg-red-500 text-white">Football</Badge>
                                    <h3 className="font-semibold text-sm leading-snug group-hover:text-primary transition-colors line-clamp-2">{item.headline}</h3>
                                    <p className="text-xs text-muted-foreground mt-1">{fmtIssued(item.issued)}</p>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Bottom grid */}
            {grid.length > 0 && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {grid.map(item => {
                        const img = itemImage(item, '16x9');
                        const id = extractArticleId(item);
                        return (
                            <button
                                key={id || item.headline}
                                onClick={() => id && onArticleClick(id)}
                                className="text-left group rounded-xl overflow-hidden border bg-card hover:border-primary/50 transition-colors"
                            >
                                {img ? (
                                    <div className="aspect-video overflow-hidden">
                                        <img src={img} alt={item.headline} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                    </div>
                                ) : (
                                    <div className="aspect-video bg-muted flex items-center justify-center">
                                        <Globe className="h-6 w-6 text-muted-foreground" />
                                    </div>
                                )}
                                <div className="p-3">
                                    <h3 className="font-semibold text-sm leading-snug group-hover:text-primary transition-colors line-clamp-2">{item.headline}</h3>
                                    <p className="text-xs text-muted-foreground mt-1">{fmtIssued(item.issued)}</p>
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ── Fixtures Section ──────────────────────────────────────────────────────────

// ── Team badge ────────────────────────────────────────────────────────────────

function TeamBadge({ teamId, teamName, size = 'sm' }) {
    const [err, setErr] = useState(false);
    const sz = size === 'lg' ? 'w-8 h-8' : 'w-6 h-6';
    if (!teamId || err) {
        return (
            <div className={`${sz} rounded-full bg-muted flex items-center justify-center flex-shrink-0`}>
                <span className="text-xs font-bold text-muted-foreground leading-none">
                    {(teamName || '?')[0].toUpperCase()}
                </span>
            </div>
        );
    }
    return (
        <img
            src={badgeUrl(teamId)}
            alt={teamName}
            className={`${sz} object-contain flex-shrink-0`}
            onError={() => setErr(true)}
        />
    );
}

// ── Fixtures Carousel ─────────────────────────────────────────────────────────

const CARD_W = 176; // px — matches w-44 (11rem = 176px)
const CARD_GAP = 12; // px — gap-3

function MatchCard({ match, anchor }) {
    const navigate = useNavigate();
    const isLive = match.status === 'live';
    const isFinished = match.status === 'finished';
    const hasScore = match.home_score != null && match.away_score != null;

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/football-hub/match/${match.pa_match_id}`, { state: { match } })}
            onKeyDown={(e) => e.key === 'Enter' && navigate(`/football-hub/match/${match.pa_match_id}`, { state: { match } })}
            className={`flex-shrink-0 w-44 rounded-xl border p-3 bg-card transition-colors cursor-pointer select-none
                ${isLive ? 'border-green-500/50 bg-green-500/5' : ''}
                ${anchor ? 'ring-2 ring-primary/40' : 'hover:bg-muted/40'}`}
        >
            {/* Date / time / round */}
            <div className="mb-2.5 space-y-0.5">
                <div className="flex items-center justify-between gap-1">
                    <span className="text-xs text-muted-foreground leading-none">
                        {fmtMatchDate(match.match_date)}
                        {!isFinished && !isLive && match.kickoff_time
                            ? `, ${fmtKickoff(match.kickoff_time)}` : ''}
                    </span>
                    {isLive && (
                        <span className="flex items-center gap-1 text-xs font-semibold text-green-500">
                            <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse inline-block" />
                            {match.phase_label || 'Live'}
                        </span>
                    )}
                    {isFinished && !isLive && (
                        <span className="text-xs text-muted-foreground font-medium">
                            {match.phase_label || 'FT'}
                        </span>
                    )}
                </div>
                {match.round && (
                    <p className="text-xs text-muted-foreground truncate">{match.round}</p>
                )}
            </div>

            {/* Home team */}
            <div className="flex items-center justify-between gap-1 mb-1.5">
                <div className="flex items-center gap-1.5 min-w-0">
                    <TeamIntelTooltip teamId={match.home_team_id} teamName={match.home_team_name}>
                        <span onClick={(e) => { e.stopPropagation(); navigate(`/football-hub/team/${match.home_team_id}`, { state: { teamName: match.home_team_name } }); }} className="cursor-pointer">
                            <TeamBadge teamId={match.home_team_id} teamName={match.home_team_name} />
                        </span>
                    </TeamIntelTooltip>
                    <span className={`text-sm font-medium truncate ${isLive ? 'text-green-500' : ''}`}>
                        {match.home_team_name}
                    </span>
                </div>
                {hasScore && (
                    <span className={`text-sm font-bold tabular-nums flex-shrink-0 ${isLive ? 'text-green-500' : ''}`}>
                        {match.home_score}
                    </span>
                )}
            </div>

            {/* Away team */}
            <div className="flex items-center justify-between gap-1">
                <div className="flex items-center gap-1.5 min-w-0">
                    <TeamIntelTooltip teamId={match.away_team_id} teamName={match.away_team_name}>
                        <span onClick={(e) => { e.stopPropagation(); navigate(`/football-hub/team/${match.away_team_id}`, { state: { teamName: match.away_team_name } }); }} className="cursor-pointer">
                            <TeamBadge teamId={match.away_team_id} teamName={match.away_team_name} />
                        </span>
                    </TeamIntelTooltip>
                    <span className={`text-sm font-medium truncate ${isLive ? 'text-green-500' : ''}`}>
                        {match.away_team_name}
                    </span>
                </div>
                {hasScore && (
                    <span className={`text-sm font-bold tabular-nums flex-shrink-0 ${isLive ? 'text-green-500' : ''}`}>
                        {match.away_score}
                    </span>
                )}
            </div>

        </div>
    );
}

function FixturesCarousel({ matches, loading }) {
    const scrollRef = useRef(null);
    const SCROLL_AMOUNT = (CARD_W + CARD_GAP) * 3;

    const scroll = (dir) =>
        scrollRef.current?.scrollBy({ left: dir * SCROLL_AMOUNT, behavior: 'smooth' });

    // Sort all matches chronologically
    const sorted = useMemo(() =>
        [...(matches || [])].sort((a, b) =>
            (a.kickoff_time || a.match_date || '').localeCompare(b.kickoff_time || b.match_date || '')
        ),
    [matches]);

    // Index of the anchor card: last live match, or last finished, or first upcoming
    const anchorIdx = useMemo(() => {
        const liveIdx = [...sorted].map((m, i) => ({ m, i }))
            .filter(({ m }) => m.status === 'live')
            .pop()?.i;
        if (liveIdx != null) return liveIdx;

        const finishedIdx = [...sorted].map((m, i) => ({ m, i }))
            .filter(({ m }) => m.status === 'finished')
            .pop()?.i;
        if (finishedIdx != null) return finishedIdx;

        return 0;
    }, [sorted]);

    // On mount (and when data loads), scroll so the anchor card is centred
    useEffect(() => {
        if (!scrollRef.current || !sorted.length) return;
        const el = scrollRef.current;
        const cardOffset = anchorIdx * (CARD_W + CARD_GAP);
        const centreOffset = el.clientWidth / 2 - CARD_W / 2;
        el.scrollLeft = cardOffset - centreOffset;
    }, [sorted.length, anchorIdx]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) {
        return (
            <div className="flex gap-3 overflow-hidden">
                {[...Array(7)].map((_, i) => (
                    <Skeleton key={i} className="flex-shrink-0 w-44 h-[104px] rounded-xl" />
                ))}
            </div>
        );
    }

    if (!sorted.length) return null;

    return (
        <div className="flex items-center gap-2">
            <Button
                variant="outline"
                size="icon"
                className="flex-shrink-0 h-8 w-8 rounded-full"
                onClick={() => scroll(-1)}
            >
                <ChevronLeft className="h-4 w-4" />
            </Button>

            <div
                ref={scrollRef}
                className="flex gap-3 overflow-x-auto flex-1"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
                {sorted.map((m, i) => (
                    <MatchCard key={m.pa_match_id} match={m} anchor={i === anchorIdx} />
                ))}
            </div>

            <Button
                variant="outline"
                size="icon"
                className="flex-shrink-0 h-8 w-8 rounded-full"
                onClick={() => scroll(1)}
            >
                <ChevronRight className="h-4 w-4" />
            </Button>
        </div>
    );
}

// ── Fixtures/Results List (whole-league, no team filter) ──────────────────────

function MatchListRow({ match }) {
    const navigate = useNavigate();
    const isLive = match.status === 'live';
    const isFinished = match.status === 'finished';
    const hasScore = match.home_score != null && match.away_score != null;

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/football-hub/match/${match.pa_match_id}`, { state: { match } })}
            onKeyDown={(e) => e.key === 'Enter' && navigate(`/football-hub/match/${match.pa_match_id}`, { state: { match } })}
            className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-muted/40 transition-colors ${isLive ? 'bg-green-500/5' : ''}`}
        >
            <div className="w-14 flex-shrink-0 text-xs text-muted-foreground">
                {isLive ? (
                    <span className="flex items-center gap-1 font-semibold text-green-500">
                        <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse inline-block" />
                        {match.phase_label || 'Live'}
                    </span>
                ) : isFinished ? (
                    <span className="font-medium">{match.phase_label || 'FT'}</span>
                ) : (
                    <span>{fmtKickoff(match.kickoff_time)}</span>
                )}
            </div>
            <div className="flex-1 flex items-center justify-end gap-2 min-w-0">
                <span className={`text-sm font-medium truncate text-right ${isLive ? 'text-green-500' : ''}`}>{match.home_team_name}</span>
                <TeamBadge teamId={match.home_team_id} teamName={match.home_team_name} />
            </div>
            <div className="flex-shrink-0 text-sm font-bold tabular-nums w-14 text-center">
                {hasScore ? `${match.home_score}–${match.away_score}` : 'v'}
            </div>
            <div className="flex-1 flex items-center gap-2 min-w-0">
                <TeamBadge teamId={match.away_team_id} teamName={match.away_team_name} />
                <span className={`text-sm font-medium truncate ${isLive ? 'text-green-500' : ''}`}>{match.away_team_name}</span>
            </div>
        </div>
    );
}

function MatchListView({ matches, mode, loading }) {
    const groups = useMemo(() => {
        const list = mode === 'results'
            ? matches.filter(m => m.status === 'finished')
            : matches.filter(m => m.status !== 'finished');
        const key = m => m.kickoff_time || m.match_date || '';
        const sorted = [...list].sort((a, b) => mode === 'results' ? key(b).localeCompare(key(a)) : key(a).localeCompare(key(b)));

        // Group into consecutive runs by day — the sort above already keeps
        // each day's matches together, so a single pass is enough.
        const out = [];
        sorted.forEach(m => {
            const dateKey = m.match_date || 'unknown';
            const lastGroup = out[out.length - 1];
            if (lastGroup && lastGroup.dateKey === dateKey) {
                lastGroup.matches.push(m);
            } else {
                out.push({ dateKey, matches: [m] });
            }
        });
        return out;
    }, [matches, mode]);

    if (loading) {
        return (
            <div className="rounded-xl border bg-card divide-y">
                {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-12 m-2" />)}
            </div>
        );
    }

    if (!groups.length) {
        return (
            <Card>
                <CardContent className="py-8 text-center text-muted-foreground text-sm">
                    {mode === 'results' ? 'No results yet for this season.' : 'No fixtures remaining this season.'}
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="rounded-xl border bg-card overflow-hidden">
            {groups.map(g => (
                <div key={g.dateKey}>
                    <div className="px-4 py-1.5 bg-primary text-primary-foreground text-xs font-bold">
                        {fmtMatchDate(g.dateKey)}
                    </div>
                    <div className="divide-y">
                        {g.matches.map(m => <MatchListRow key={m.pa_match_id} match={m} />)}
                    </div>
                </div>
            ))}
        </div>
    );
}

// ── League Table Section ──────────────────────────────────────────────────────

// Maps battleLine @comment strings to a left-border colour class
function zoneColor(zone) {
    if (!zone) return '';
    const z = zone.toLowerCase();
    if (z.includes('champions league'))  return 'border-l-2 border-l-blue-500';
    if (z.includes('europa league'))     return 'border-l-2 border-l-orange-400';
    if (z.includes('conference league')) return 'border-l-2 border-l-teal-400';
    if (z.includes('relegation') || z.includes('championship')) return 'border-l-2 border-l-red-500';
    if (z.includes('promotion'))         return 'border-l-2 border-l-green-500';
    return 'border-l-2 border-l-muted';
}

function GroupTable({ group }) {
    const navigate = useNavigate();
    // Detect zone changes so we can insert a subtle divider
    const rows = group.rows || [];
    let prevZone = rows[0]?.zone ?? null;

    return (
        <div>
            {group.name && (
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-1 mb-1">
                    {group.name}
                </p>
            )}
            <table className="w-full text-xs">
                <thead>
                    <tr className="text-muted-foreground border-b">
                        <th className="text-left font-normal pb-1 w-6 pl-2">#</th>
                        <th className="text-left font-normal pb-1">Team</th>
                        <th className="text-center font-normal pb-1 w-6">P</th>
                        <th className="text-center font-normal pb-1 w-6">W</th>
                        <th className="text-center font-normal pb-1 w-6">D</th>
                        <th className="text-center font-normal pb-1 w-6">L</th>
                        <th className="text-center font-normal pb-1 w-8">GD</th>
                        <th className="text-center font-bold pb-1 w-8">Pts</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, idx) => {
                        const zoneChanged = idx > 0 && row.zone !== prevZone;
                        prevZone = row.zone;
                        return (
                            <tr
                                key={row.team_id || row.team}
                                className={`border-b last:border-0 hover:bg-muted/30 ${zoneChanged ? 'border-t border-t-border/60' : ''}`}
                            >
                                {/* Position with zone colour bar */}
                                <td className={`py-1.5 pl-2 text-muted-foreground ${row.zone ? zoneColor(row.zone) : ''}`}>
                                    {row.position}
                                </td>
                                {/* Badge + name */}
                                <td className="py-1 pr-1">
                                    <div className="flex items-center gap-1.5 min-w-0">
                                        <span
                                            className="cursor-pointer"
                                            onClick={(e) => { e.stopPropagation(); if (row.team_id) navigate(`/football-hub/team/${row.team_id}`, { state: { teamName: row.team } }); }}
                                        >
                                            <TeamBadge teamId={row.team_id} teamName={row.team} size="sm" />
                                        </span>
                                        <span className="font-medium truncate">{row.team}</span>
                                    </div>
                                </td>
                                <td className="py-1.5 text-center text-muted-foreground">{row.played}</td>
                                <td className="py-1.5 text-center text-muted-foreground">{row.won}</td>
                                <td className="py-1.5 text-center text-muted-foreground">{row.drawn}</td>
                                <td className="py-1.5 text-center text-muted-foreground">{row.lost}</td>
                                <td className={`py-1.5 text-center ${row.gd > 0 ? 'text-green-500' : row.gd < 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                                    {row.gd > 0 ? `+${row.gd}` : row.gd}
                                </td>
                                <td className="py-1.5 text-center font-bold">{row.points}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function TableSection({ groups, loading }) {
    const [showAll, setShowAll] = useState(false);

    if (loading) {
        return (
            <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base flex gap-2 items-center"><Trophy className="h-4 w-4" />Table</CardTitle></CardHeader>
                <CardContent className="pt-0 space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</CardContent>
            </Card>
        );
    }

    if (!groups?.length) {
        return (
            <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base flex gap-2 items-center"><Trophy className="h-4 w-4" />Table</CardTitle></CardHeader>
                <CardContent className="py-6 text-center text-muted-foreground text-sm">Table not available yet.</CardContent>
            </Card>
        );
    }

    // Single-group (league): show top 10, expand for rest
    // Multi-group (tournament): show first group, expand for others
    const isMultiGroup = groups.length > 1;
    const primary = groups[0];
    const rest = groups.slice(1);

    // For a single group with many rows, truncate at 10
    const primaryRows = primary.rows || [];
    const truncated = !isMultiGroup && !showAll && primaryRows.length > 10;
    const primaryGroup = truncated
        ? { ...primary, rows: primaryRows.slice(0, 10) }
        : primary;

    const canExpand = isMultiGroup ? rest.length > 0 : primaryRows.length > 10;
    const expandLabel = isMultiGroup
        ? (showAll ? 'Hide groups' : `View all ${groups.length} groups`)
        : (showAll ? 'Show less' : `Show all ${primaryRows.length} teams`);

    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                    <Trophy className="h-4 w-4 text-amber-400" />
                    League Table
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-4">
                <GroupTable group={primaryGroup} />

                {showAll && isMultiGroup && rest.map(g => (
                    <div key={g.name} className="border-t pt-3">
                        <GroupTable group={g} />
                    </div>
                ))}

                {canExpand && (
                    <button
                        onClick={() => setShowAll(v => !v)}
                        className="w-full flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors pt-1"
                    >
                        {showAll
                            ? <><ChevronUp className="h-3.5 w-3.5" />{expandLabel}</>
                            : <><ChevronDown className="h-3.5 w-3.5" />{expandLabel}</>
                        }
                    </button>
                )}
            </CardContent>
        </Card>
    );
}

// ── Top Scorers Section ───────────────────────────────────────────────────────

function PlayerPhoto({ playerId, playerName, useWc = false }) {
    // Club photo for club comps, WC photo for the World Cup — each falls back
    // to the other when the player has no photo of that kind.
    const { src, isWcImg } = usePlayerPhoto(playerId, useWc);
    if (!src) {
        return (
            <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-muted-foreground">
                    {(playerName || '?')[0].toUpperCase()}
                </span>
            </div>
        );
    }
    if (isWcImg) {
        return (
            <div className="w-7 h-7 rounded-full flex-shrink-0 bg-muted"
                style={{ backgroundImage: `url(${src})`, backgroundSize: 'auto 230%',
                         backgroundPosition: '50% 5%', backgroundRepeat: 'no-repeat' }}
                role="img" aria-label={playerName} />
        );
    }
    return (
        <img src={src} alt={playerName}
            className="w-7 h-7 rounded-full object-cover flex-shrink-0 bg-muted" />
    );
}

// ── Tournament Bracket ────────────────────────────────────────────────────────

// A team name is still a placeholder feeder (e.g. "Winner Match 73") until the
// real team is known — show it muted with a "?" rather than a missing badge.
const _isPlaceholderTeam = (name) => /^(winner|loser|runner|group)\b/i.test((name || '').trim());

function BracketBadge({ teamId, name }) {
    const [err, setErr] = useState(false);
    if (_isPlaceholderTeam(name) || err || !teamId) {
        return (
            <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-[9px] text-muted-foreground shrink-0">?</div>
        );
    }
    return <img src={badgeUrl(teamId)} alt={name} className="w-5 h-5 object-contain shrink-0" onError={() => setErr(true)} />;
}

// Elbow connector paths between a "many" column (manyN boxes) and the "few"
// column (manyN/2 boxes) it feeds. `toward` = which side the few column is on.
function _bracketConnectorPaths(manyN, toward, W, H) {
    const fewN = manyN / 2;
    const cy = i => H * (2 * i + 1) / (2 * manyN);
    const fy = j => H * (2 * j + 1) / (2 * fewN);
    const paths = [];
    for (let j = 0; j < fewN; j++) {
        const yTop = cy(2 * j), yBot = cy(2 * j + 1), yMid = fy(j);
        if (toward === 'right') {
            paths.push(`M0 ${yTop} H${W / 2} M0 ${yBot} H${W / 2} M${W / 2} ${yTop} V${yBot} M${W / 2} ${yMid} H${W}`);
        } else {
            paths.push(`M${W} ${yTop} H${W / 2} M${W} ${yBot} H${W / 2} M${W / 2} ${yTop} V${yBot} M${W / 2} ${yMid} H0`);
        }
    }
    return paths.join(' ');
}

// One line: home badge · ABBR · score–score · ABBR · away badge; date/time below
function BracketMatch({ m, onOpen }) {
    const fin = m.status === 'finished', live = m.status === 'live';
    const dec = fin && m.home_score != null && m.away_score != null;
    const hw = dec && m.home_score > m.away_score, aw = dec && m.away_score > m.home_score;
    const hPh = _isPlaceholderTeam(m.home_team_name), aPh = _isPlaceholderTeam(m.away_team_name);
    const played = m.home_score != null && m.away_score != null;
    const mid = played ? `${m.home_score}–${m.away_score}` : 'v';
    let when = '';
    try { if (m.kickoff_time) when = format(parseISO(m.kickoff_time), 'd MMM HH:mm'); } catch { /* noop */ }
    return (
        <div onClick={played && onOpen ? () => onOpen(m.pa_match_id) : undefined}
            className={`bg-card border rounded shadow-sm px-1.5 py-1 ${live ? 'border-red-500/50' : ''} ${played && onOpen ? 'cursor-pointer hover:border-primary/60' : ''}`}>
            <div className="flex items-center justify-center gap-1 text-[11px] leading-tight">
                <BracketBadge teamId={m.home_team_id} name={m.home_team_name} />
                <span className={`font-semibold ${hPh ? 'text-muted-foreground' : hw ? 'font-bold' : ''}`}>{teamAbbr(m.home_team_name)}</span>
                <span className="text-muted-foreground font-semibold tabular-nums px-0.5">{mid}</span>
                <span className={`font-semibold ${aPh ? 'text-muted-foreground' : aw ? 'font-bold' : ''}`}>{teamAbbr(m.away_team_name)}</span>
                <BracketBadge teamId={m.away_team_id} name={m.away_team_name} />
            </div>
            <div className="text-center text-[9px] text-muted-foreground leading-tight mt-0.5">
                {live ? <span className="text-red-500 font-semibold">● {m.phase_label || 'LIVE'}</span> : when}
            </div>
        </div>
    );
}

function TournamentBracket({ rounds, loading }) {
    const [openMatch, setOpenMatch] = useState(null);
    if (loading) {
        return (
            <div className="flex gap-6 overflow-x-auto pb-2">
                {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-64 w-56 flex-shrink-0" />)}
            </div>
        );
    }
    if (!rounds?.length) {
        return <Card><CardContent className="py-8 text-center text-muted-foreground text-sm">Bracket not available yet — knockout fixtures appear once the group stage is complete.</CardContent></Card>;
    }
    // Two-sided bracket: left half (R32→SF) flows right, right half mirrors it, the
    // Final + 3rd-place sit in the centre — both halves' journey to the final.
    const byRank = (rank) => rounds.find(r => r.rank === rank) || { matches: [] };
    const side = (rank, s) => byRank(rank).matches.filter(m => m.side === s);
    const finalRound = byRank(6), thirdRound = byRank(5);

    const SLOT = 52;
    const colHeight = 8 * SLOT;
    const CW = 16; // connector width

    const Column = ({ name, matches }) => (
        <div className="flex flex-col flex-1 min-w-0">
            <h3 className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground whitespace-nowrap mb-2 h-4 text-center">{name}</h3>
            <div className="flex flex-col justify-around" style={{ height: colHeight }}>
                {matches.map(m => <BracketMatch key={m.pa_match_id} m={m} onOpen={setOpenMatch} />)}
            </div>
        </div>
    );
    const Conn = ({ manyN, toward }) => (
        <div className="flex flex-col flex-shrink-0" style={{ width: CW }}>
            <div className="h-4 mb-2" />
            <svg width={CW} height={colHeight} style={{ overflow: 'visible' }}>
                <path d={_bracketConnectorPaths(manyN, toward, CW, colHeight)} stroke="hsl(var(--border))" strokeWidth="1.5" fill="none" />
            </svg>
        </div>
    );
    const Stub = ({ toward }) => (
        <div className="flex flex-col flex-shrink-0" style={{ width: CW }}>
            <div className="h-4 mb-2" />
            <svg width={CW} height={colHeight} style={{ overflow: 'visible' }}>
                <path d={`M${toward === 'right' ? 0 : CW} ${colHeight / 2} H${toward === 'right' ? CW : 0}`} stroke="hsl(var(--border))" strokeWidth="1.5" fill="none" />
            </svg>
        </div>
    );

    return (
        <div className="w-full overflow-x-auto">
            <div className="flex items-start" style={{ minWidth: 'min(100%, 1000px)' }}>
                <Column name="R32" matches={side(1, 'left')} />
                <Conn manyN={8} toward="right" />
                <Column name="R16" matches={side(2, 'left')} />
                <Conn manyN={4} toward="right" />
                <Column name="QF" matches={side(3, 'left')} />
                <Conn manyN={2} toward="right" />
                <Column name="SF" matches={side(4, 'left')} />
                <Stub toward="right" />
                <div className="flex flex-col flex-shrink-0 justify-center px-1" style={{ width: 124, height: colHeight + 16 }}>
                    {finalRound.matches.map(m => (
                        <div key={m.pa_match_id} className="mb-3">
                            <p className="text-center text-[11px] font-bold mb-1">FINAL</p>
                            <BracketMatch m={m} onOpen={setOpenMatch} />
                        </div>
                    ))}
                    {thirdRound.matches.map(m => (
                        <div key={m.pa_match_id}>
                            <p className="text-center text-[9px] text-muted-foreground font-semibold mb-1">3rd place</p>
                            <BracketMatch m={m} onOpen={setOpenMatch} />
                        </div>
                    ))}
                </div>
                <Stub toward="left" />
                <Column name="SF" matches={side(4, 'right')} />
                <Conn manyN={2} toward="left" />
                <Column name="QF" matches={side(3, 'right')} />
                <Conn manyN={4} toward="left" />
                <Column name="R16" matches={side(2, 'right')} />
                <Conn manyN={8} toward="left" />
                <Column name="R32" matches={side(1, 'right')} />
            </div>
            {openMatch && <BracketMatchModal matchId={openMatch} onClose={() => setOpenMatch(null)} />}
        </div>
    );
}

function TopScorersSection({ scorers, loading, onPlayerClick, groups, isWc = false }) {
    const [showAll, setShowAll] = useState(false);
    const VISIBLE = 10;

    // Must be before early returns — hooks cannot be called conditionally
    // Build two lookups from WC groups table data:
    //   wcTeamById:   team_id  → {name, team_id}  (direct ID match)
    //   wcTeamByName: "mexico" → {name, team_id}  (nationality string match)
    const { wcTeamById, wcTeamByName } = useMemo(() => {
        const wcTeamById = {}, wcTeamByName = {};
        (groups || []).forEach(g =>
            (g.rows || []).forEach(r => {
                if (!r.team_id || !r.team) return;
                const val = { name: r.team, team_id: String(r.team_id) };
                wcTeamById[String(r.team_id)] = val;
                wcTeamByName[r.team.toLowerCase()] = val;
            })
        );
        return { wcTeamById, wcTeamByName };
    }, [groups]);

    const resolveNatTeam = useCallback((s) => {
        // 1. Direct team_id match against WC groups
        if (s.team_id && wcTeamById[String(s.team_id)]) return wcTeamById[String(s.team_id)];
        // 2. Nationality string → WC team name (handles "Mexican"→"Mexico", "South African"→"South Africa")
        if (s.nationality) {
            const n = s.nationality.toLowerCase();
            for (const [tname, val] of Object.entries(wcTeamByName)) {
                if (n === tname || n.startsWith(tname) || tname.startsWith(n)) return val;
            }
        }
        // 3. Team name direct match (e.g. s.team = "Mexico")
        if (s.team && wcTeamByName[s.team.toLowerCase()]) return wcTeamByName[s.team.toLowerCase()];
        return null;
    }, [wcTeamById, wcTeamByName]);

    const shown = showAll ? scorers : (scorers || []).slice(0, VISIBLE);

    if (loading) {
        return (
            <Card className="h-full">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex gap-2 items-center">
                        <Star className="h-4 w-4 text-amber-400" />Top Scorers
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                    {[...Array(VISIBLE)].map((_, i) => (
                        <div key={i} className="flex items-center gap-2 px-4 py-1.5 border-b last:border-0">
                            <Skeleton className="w-5 h-3 flex-shrink-0" />
                            <Skeleton className="w-7 h-7 rounded-md flex-shrink-0" />
                            <div className="flex-1 space-y-1">
                                <Skeleton className="h-3 w-28" />
                                <Skeleton className="h-3 w-20" />
                            </div>
                            <Skeleton className="w-6 h-4 flex-shrink-0" />
                        </div>
                    ))}
                </CardContent>
            </Card>
        );
    }

    if (!scorers?.length) {
        return (
            <Card className="h-full">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex gap-2 items-center">
                        <Star className="h-4 w-4 text-amber-400" />Top Scorers
                    </CardTitle>
                </CardHeader>
                <CardContent className="py-6 text-center text-muted-foreground text-sm">
                    No scorer data yet.
                </CardContent>
            </Card>
        );
    }

    // Assign sequential ranks (tied players share a rank)
    const ranked = shown.map((s, i, arr) => ({
        ...s,
        rank: i === 0 ? 1 : (s.goals === arr[i - 1].goals ? arr[i - 1]._rank : i + 1),
        _rank: i === 0 ? 1 : (s.goals === arr[i - 1].goals ? arr[i - 1]._rank : i + 1),
    }));

    return (
        <Card className="h-full flex flex-col">
            <CardHeader className="pb-2 flex-shrink-0">
                <CardTitle className="text-base flex items-center gap-2">
                    <Star className="h-4 w-4 text-amber-400" />
                    Top Scorers
                </CardTitle>
            </CardHeader>
            <CardContent className="p-0 flex-1 flex flex-col">
                <div className="flex-1">
                    {ranked.map((s) => {
                        const natTeam = resolveNatTeam(s);
                        const displayTeam = natTeam?.name || s.team || '';
                        const displayTeamId = natTeam?.team_id || s.team_id || '';
                        return (
                            <div
                                key={s.player_id || s.player}
                                role="button"
                                tabIndex={0}
                                onClick={() => s.player_id && onPlayerClick?.({ player_id: s.player_id, player: s.player, team_id: displayTeamId })}
                                onKeyDown={e => e.key === 'Enter' && s.player_id && onPlayerClick?.({ player_id: s.player_id, player: s.player, team_id: displayTeamId })}
                                className={`flex items-center gap-2 px-4 py-1.5 border-b last:border-0 transition-colors ${s.player_id ? 'cursor-pointer hover:bg-muted/30' : ''}`}
                            >
                                <span className="text-xs text-muted-foreground w-5 flex-shrink-0 text-center tabular-nums">
                                    {s.rank}
                                </span>
                                <PlayerPhoto playerId={s.player_id} playerName={s.player} useWc={isWc && !!natTeam} />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-semibold truncate leading-tight">{s.player}</p>
                                    <div className="flex items-center gap-1">
                                        {displayTeamId && <TeamBadge teamId={displayTeamId} teamName={displayTeam} size="sm" />}
                                        {displayTeam && <p className="text-xs text-muted-foreground truncate">{displayTeam}</p>}
                                    </div>
                                </div>
                                <span className="text-sm font-bold tabular-nums flex-shrink-0">{s.goals}</span>
                            </div>
                        );
                    })}
                </div>

                {(scorers?.length || 0) > VISIBLE && (
                    <button
                        onClick={() => setShowAll(v => !v)}
                        className="w-full flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors py-2.5 border-t flex-shrink-0"
                    >
                        {showAll
                            ? <><ChevronUp className="h-3.5 w-3.5" /> Show less</>
                            : <><ChevronDown className="h-3.5 w-3.5" /> Show all {scorers.length}</>
                        }
                    </button>
                )}
            </CardContent>
        </Card>
    );
}

// ── My Teams ─────────────────────────────────────────────────────────────────

function MyTeamCard({ team, navigate }) {
    const [badgeErr, setBadgeErr] = useState(false);
    const next = team.next_match;
    const last = team.last_result;
    const isHome = next && String(next.home_team_id) === String(team.team_id);
    const opponent = next ? (isHome ? next.away_team_name : next.home_team_name) : null;
    const opponentId = next ? (isHome ? next.away_team_id : next.home_team_id) : null;
    const [oppErr, setOppErr] = useState(false);

    // Last result outcome
    let outcome = null;
    if (last) {
        const weHome = String(last.home_team_id) === String(team.team_id);
        const ourScore = weHome ? last.home_score : last.away_score;
        const theirScore = weHome ? last.away_score : last.home_score;
        if (ourScore > theirScore) outcome = 'W';
        else if (ourScore < theirScore) outcome = 'L';
        else outcome = 'D';
    }

    return (
        <button
            onClick={() => navigate(`/football-hub/team/${team.team_id}`)}
            className="flex flex-col gap-3 p-4 rounded-2xl border bg-card hover:bg-muted/40 transition-colors text-left w-full"
        >
            <div className="flex items-center gap-3">
                {!badgeErr ? (
                    <img src={badgeUrl(team.team_id)} alt={team.team_name}
                        className="w-10 h-10 object-contain flex-shrink-0"
                        onError={() => setBadgeErr(true)} />
                ) : (
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-muted-foreground">{team.team_name?.[0]}</span>
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{team.team_name}</p>
                    {last && outcome && (
                        <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                            outcome === 'W' ? 'bg-green-500/20 text-green-500' :
                            outcome === 'L' ? 'bg-red-500/20 text-red-500' :
                            'bg-muted text-muted-foreground'
                        }`}>{outcome} {last.home_score}–{last.away_score}</span>
                    )}
                </div>
            </div>
            {next ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {!oppErr ? (
                        <img src={badgeUrl(opponentId)} alt={opponent}
                            className="w-5 h-5 object-contain flex-shrink-0"
                            onError={() => setOppErr(true)} />
                    ) : (
                        <div className="w-5 h-5 rounded-full bg-muted flex-shrink-0" />
                    )}
                    <span className="truncate">vs {opponent}</span>
                    <span className="ml-auto flex-shrink-0 font-medium">
                        {next.date ? format(parseISO(next.date), 'EEE d MMM') : ''}
                    </span>
                </div>
            ) : (
                <p className="text-xs text-muted-foreground">No upcoming fixture</p>
            )}
        </button>
    );
}

function MyTeamsSection({ navigate }) {
    const [teams, setTeams] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get(`${API_URL}/api/football/following/teams`, { headers: authHeaders() })
            .then(r => setTeams(r.data.teams || []))
            .catch(() => setTeams([]))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return null; // silent load
    if (!teams || teams.length === 0) return null; // hidden until following someone

    return (
        <section>
            <div className="flex items-center gap-2 mb-3">
                <div className="w-1 h-5 bg-rose-500 rounded-full" />
                <h2 className="font-bold text-lg flex items-center gap-1.5">
                    <Heart className="h-4 w-4 text-rose-500 fill-rose-500" />
                    My Teams
                </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {teams.map(t => (
                    <MyTeamCard key={t.team_id} team={t} navigate={navigate} />
                ))}
            </div>
        </section>
    );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function FootballHubPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    // null = settings not yet loaded; avoids a double-fetch race when the
    // hardcoded default differs from (or briefly races with) the real setting
    const [hubCompId, setHubCompId] = useState(null);
    const [compName, setCompName] = useState('Football Hub');
    const [news, setNews] = useState([]);
    const [newsLoading, setNewsLoading] = useState(true);
    const [fixtures, setFixtures] = useState([]);
    const [fixturesLoading, setFixturesLoading] = useState(true);
    const [groups, setGroups] = useState([]);
    const [tableLoading, setTableLoading] = useState(true);
    const [scorers, setScorers] = useState([]);
    const [scorersLoading, setScorersLoading] = useState(true);
    const [showBrackets, setShowBrackets] = useState(false);
    const [bracket, setBracket] = useState([]);
    const [bracketLoading, setBracketLoading] = useState(true);
    const [articleId, setArticleId] = useState(null);
    const [profilePlayer, setProfilePlayer] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [teamPickerOpen, setTeamPickerOpen] = useState(false);
    const [filteredTeam, setFilteredTeam] = useState(null); // { id, name } | null
    const [resultsView, setResultsView] = useState('carousel'); // 'carousel' | 'fixtures-list' | 'results-list'
    const [fullSeasonMatches, setFullSeasonMatches] = useState([]);
    const [fullSeasonLoading, setFullSeasonLoading] = useState(false);
    // Bumped on every fetchAll call so a slower, older request (e.g. a past
    // season's table, which does more work than the current one) can't
    // overwrite state with stale data if it resolves after a newer request.
    const fetchSeqRef = useRef(0);

    // League switcher + season browser
    const [competitions, setCompetitions] = useState([]);
    const [competitionsLoading, setCompetitionsLoading] = useState(true);
    const [favouriteComps, setFavouriteComps] = useState([]); // [{ comp_id, comp_name }]
    const [leaguePickerOpen, setLeaguePickerOpen] = useState(false);
    const [seasons, setSeasons] = useState([]);
    const [seasonsLoading, setSeasonsLoading] = useState(false);
    const [selectedSeasonId, setSelectedSeasonId] = useState(null); // null = current season
    // seasons[0] is always the newest/current season (backend returns newest-first) —
    // explicitly picking it from the dropdown is not "historical", only an older one is
    const currentSeasonId = seasons[0]?.id != null ? String(seasons[0].id) : null;
    const isHistoricalSeason = selectedSeasonId !== null && String(selectedSeasonId) !== currentSeasonId;
    const activeSeason = seasons.find(s => String(s.id) === String(selectedSeasonId || currentSeasonId));
    const activeSeasonLabel = activeSeason ? shortSeasonLabel(activeSeason.name, compName) : '';

    // On mount: if we're arriving via the browser back button from a match
    // page, location.state carries exactly what the user was looking at
    // (league, season, results-list view, team filter) — restore it instead
    // of re-fetching the site's default league. Otherwise fetch settings as
    // normal (fresh visit via the nav bar, or a bookmarked/refreshed URL).
    useEffect(() => {
        const saved = location.state?.hubView;
        if (saved) {
            setCompName(saved.compName || 'Football Hub');
            setSelectedSeasonId(saved.selectedSeasonId ?? null);
            setResultsView(saved.resultsView || 'carousel');
            setFilteredTeam(saved.filteredTeam || null);
            setHubCompId(saved.hubCompId); // triggers fetchAll via the hubCompId effect below
            // show_brackets is a site setting, not part of the user's view state —
            // fetch it too so a restored WC page still gets the bracket layout
            axios.get(`${API_URL}/api/site-settings/public`)
                .then(r => setShowBrackets(r.data.show_brackets === true))
                .catch(() => {});
            return;
        }
        axios.get(`${API_URL}/api/site-settings/public`)
            .then(r => {
                const id = r.data.hub_comp_id || 700;
                setCompName(r.data.hub_comp_name || 'Football Hub');
                setShowBrackets(r.data.show_brackets === true);
                setHubCompId(id);
            })
            .catch(() => setHubCompId(700));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep the current /football-hub history entry's state in sync with what
    // the user is actually looking at, so the browser back button (from a
    // match page pushed on top of it) restores this exact view instead of
    // whatever it was when the page first loaded.
    useEffect(() => {
        if (hubCompId === null) return;
        navigate(location.pathname, {
            replace: true,
            state: { hubView: { hubCompId, compName, selectedSeasonId, resultsView, filteredTeam } },
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hubCompId, compName, selectedSeasonId, resultsView, filteredTeam]);

    // League list + favourites — independent of the currently selected league
    useEffect(() => {
        const h = authHeaders();
        setCompetitionsLoading(true);
        axios.get(`${API_URL}/api/football/hub/competitions`, { headers: h })
            .then(r => setCompetitions(r.data.competitions || []))
            .catch(() => setCompetitions([]))
            .finally(() => setCompetitionsLoading(false));

        axios.get(`${API_URL}/api/football/following/competitions`, { headers: h })
            .then(r => setFavouriteComps(r.data.competitions || []))
            .catch(() => setFavouriteComps([]));
    }, []);

    // Season list for whichever league is currently selected
    useEffect(() => {
        if (hubCompId === null) return;
        setSeasonsLoading(true);
        axios.get(`${API_URL}/api/football/hub/seasons`, { params: { comp_id: hubCompId }, headers: authHeaders() })
            .then(r => setSeasons(r.data.seasons || []))
            .catch(() => setSeasons([]))
            .finally(() => setSeasonsLoading(false));
    }, [hubCompId]);

    const fetchAll = useCallback(async (compId, seasonId, isHistorical) => {
        const seq = ++fetchSeqRef.current;
        const isStale = () => seq !== fetchSeqRef.current;

        const h = authHeaders();
        const p = `?comp_id=${compId}`;
        const sp = seasonId ? `&season_id=${seasonId}` : '';

        // News stays today's news regardless of league/season selected
        setNewsLoading(true);
        axios.get(`${API_URL}/api/football/hub/news`, { headers: h })
            .then(r => { if (!isStale()) setNews(r.data.items || []); })
            .catch(() => { if (!isStale()) setNews([]); })
            .finally(() => { if (!isStale()) setNewsLoading(false); });

        setFixturesLoading(true);
        axios.get(`${API_URL}/api/football/hub/fixtures${p}${sp}`, { headers: h })
            .then(r => { if (!isStale()) setFixtures(r.data.matches || []); })
            .catch(() => { if (!isStale()) setFixtures([]); })
            .finally(() => { if (!isStale()) setFixturesLoading(false); });

        setTableLoading(true);
        axios.get(`${API_URL}/api/football/hub/table${p}${sp}`, { headers: h })
            .then(r => { if (!isStale()) setGroups(r.data.groups || []); })
            .catch(() => { if (!isStale()) setGroups([]); })
            .finally(() => { if (!isStale()) setTableLoading(false); });

        setScorersLoading(true);
        axios.get(`${API_URL}/api/football/hub/top-scorers${p}${sp}`, { headers: h })
            .then(r => { if (!isStale()) setScorers(r.data.scorers || []); })
            .catch(() => { if (!isStale()) setScorers([]); })
            .finally(() => { if (!isStale()) setScorersLoading(false); });

        // Brackets only apply to the current tournament, never a browsed season
        if (!isHistorical) {
            setBracketLoading(true);
            axios.get(`${API_URL}/api/football/hub/bracket${p}`, { headers: h })
                .then(r => { if (!isStale()) setBracket(r.data.rounds || []); })
                .catch(() => { if (!isStale()) setBracket([]); })
                .finally(() => { if (!isStale()) setBracketLoading(false); });
        } else {
            setBracket([]);
            setBracketLoading(false);
        }
    }, []);

    // Fires whenever the league or season changes (not just once hubCompId is known)
    useEffect(() => {
        if (hubCompId !== null) fetchAll(hubCompId, selectedSeasonId, isHistoricalSeason);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hubCompId, selectedSeasonId, fetchAll]);

    // Live fixtures poll — refresh the carousel every 30 s while games are on.
    // Never applies when browsing a past season (nothing there is ever live).
    const liveCount = isHistoricalSeason ? 0 : fixtures.filter(m => m.status === 'live').length;
    useEffect(() => {
        if (!liveCount || hubCompId === null) return;
        const p = hubCompId !== 700 ? `?comp_id=${hubCompId}` : '';
        const h = authHeaders();
        const interval = setInterval(() => {
            axios.get(`${API_URL}/api/football/hub/fixtures${p}`, { headers: h })
                .then(r => setFixtures(r.data.matches || []))
                .catch(() => {});
        }, 30000);
        return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveCount, hubCompId]);

    const handleRefresh = async () => {
        if (hubCompId === null) return;
        setRefreshing(true);
        await fetchAll(hubCompId, selectedSeasonId, isHistoricalSeason);
        setRefreshing(false);
    };

    // Fetch the FULL season's fixtures (not the rolling "today" window) for the
    // whole-league Fixtures/Results list views — reuses the season-aware
    // endpoint by always passing a season_id (the current one if none picked).
    useEffect(() => {
        if (resultsView === 'carousel' || hubCompId === null) return;
        const seasonForList = selectedSeasonId || currentSeasonId;
        if (!seasonForList) return;
        let cancelled = false;
        setFullSeasonLoading(true);
        axios.get(`${API_URL}/api/football/hub/fixtures`, {
            params: { comp_id: hubCompId, season_id: seasonForList },
            headers: authHeaders(),
        })
            .then(r => { if (!cancelled) setFullSeasonMatches(r.data.matches || []); })
            .catch(() => { if (!cancelled) setFullSeasonMatches([]); })
            .finally(() => { if (!cancelled) setFullSeasonLoading(false); });
        return () => { cancelled = true; };
    }, [resultsView, hubCompId, selectedSeasonId, currentSeasonId]);

    const handleSelectLeague = (comp) => {
        setHubCompId(comp.id);
        setCompName(comp.name);
        setSelectedSeasonId(null); // back to that league's current season
        // Popover stays open — the season tab on the side updates for the newly
        // selected league so the user can immediately pick a season too.
    };

    const handleToggleFavouriteComp = async (comp, e) => {
        e.stopPropagation();
        const isFav = favouriteComps.some(c => String(c.comp_id) === String(comp.id));
        try {
            if (isFav) {
                await axios.delete(`${API_URL}/api/football/following/competitions/${comp.id}`, { headers: authHeaders() });
                setFavouriteComps(prev => prev.filter(c => String(c.comp_id) !== String(comp.id)));
            } else {
                await axios.post(`${API_URL}/api/football/following/competitions`,
                    { comp_id: comp.id, comp_name: comp.name }, { headers: authHeaders() });
                setFavouriteComps(prev => [...prev, { comp_id: comp.id, comp_name: comp.name }]);
            }
        } catch {
            // Non-critical — silently ignore, star just won't update
        }
    };

    return (
        <div className="container-app py-8 space-y-8">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        {hubCompId === 700 ? (
                            <img
                                src="https://upload.wikimedia.org/wikipedia/en/thumb/1/17/2026_FIFA_World_Cup_emblem.svg/960px-2026_FIFA_World_Cup_emblem.svg.png"
                                alt="FIFA World Cup 2026"
                                className="h-9 w-auto object-contain"
                            />
                        ) : (
                            <Globe className="h-5 w-5 text-primary" />
                        )}

                        <Popover open={leaguePickerOpen} onOpenChange={setLeaguePickerOpen}>
                            <PopoverTrigger asChild>
                                <button className="flex items-center gap-2 group" data-testid="league-picker-trigger">
                                    <h1 className="text-3xl font-bold font-heading group-hover:text-primary transition-colors page-title">
                                        {compName}
                                    </h1>
                                    {activeSeasonLabel && (
                                        <Badge variant="outline" className="text-[10px] font-semibold">
                                            {activeSeasonLabel}
                                        </Badge>
                                    )}
                                    <ChevronDown className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[520px] p-0" align="start">
                                <div className="flex" style={{ height: 380 }}>
                                    {/* Search + league list */}
                                    <Command className="flex-1 min-w-0 rounded-none border-r">
                                        <CommandInput placeholder="Search leagues..." />
                                        <CommandList className="max-h-none flex-1">
                                            <CommandEmpty>
                                                {competitionsLoading ? 'Loading leagues…' : 'No leagues found.'}
                                            </CommandEmpty>
                                            {favouriteComps.length > 0 && (
                                                <CommandGroup heading="Favourites">
                                                    {favouriteComps.map(fc => (
                                                        <CommandItem
                                                            key={`fav-${fc.comp_id}`}
                                                            value={`fav-${fc.comp_name}`}
                                                            onSelect={() => handleSelectLeague({ id: fc.comp_id, name: fc.comp_name })}
                                                        >
                                                            <Star className="h-3.5 w-3.5 mr-2 flex-shrink-0 fill-amber-400 text-amber-400" />
                                                            <span className="truncate">{fc.comp_name}</span>
                                                        </CommandItem>
                                                    ))}
                                                </CommandGroup>
                                            )}
                                            <CommandGroup heading="All Leagues">
                                                {competitions.map(c => {
                                                    const isFav = favouriteComps.some(fc => String(fc.comp_id) === String(c.id));
                                                    const isActive = String(c.id) === String(hubCompId);
                                                    return (
                                                        <CommandItem
                                                            key={c.id}
                                                            value={c.name}
                                                            onSelect={() => handleSelectLeague(c)}
                                                            className={`flex items-center justify-between gap-2 ${isActive ? 'bg-muted' : ''}`}
                                                        >
                                                            <span className="truncate">{c.name}</span>
                                                            <button
                                                                onClick={(e) => handleToggleFavouriteComp(c, e)}
                                                                onMouseDown={(e) => e.stopPropagation()}
                                                                className="flex-shrink-0 p-0.5"
                                                                aria-label={isFav ? 'Remove favourite' : 'Add favourite'}
                                                            >
                                                                <Star className={`h-3.5 w-3.5 ${isFav ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground'}`} />
                                                            </button>
                                                        </CommandItem>
                                                    );
                                                })}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>

                                    {/* Season tab — for whichever league is currently selected */}
                                    <div className="w-44 flex-shrink-0 flex flex-col">
                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground px-3 pt-3 pb-2 flex-shrink-0">
                                            Season
                                        </p>
                                        <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-0.5">
                                            {seasonsLoading ? (
                                                [...Array(5)].map((_, i) => <Skeleton key={i} className="h-7 mx-0.5" />)
                                            ) : seasons.length === 0 ? (
                                                <p className="text-xs text-muted-foreground px-2 py-4 text-center">No seasons found</p>
                                            ) : seasons.map((s, i) => {
                                                const isSelected = i === 0 ? selectedSeasonId === null : String(s.id) === String(selectedSeasonId);
                                                return (
                                                    <button
                                                        key={s.id}
                                                        onClick={() => {
                                                            setSelectedSeasonId(i === 0 ? null : String(s.id));
                                                            setLeaguePickerOpen(false);
                                                        }}
                                                        className={`w-full text-left text-xs px-2.5 py-1.5 rounded-md transition-colors ${
                                                            isSelected ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                                                        }`}
                                                    >
                                                        {shortSeasonLabel(s.name, compName)}{i === 0 ? ' (Current)' : ''}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </PopoverContent>
                        </Popover>

                        {liveCount > 0 && (
                            <span className="flex items-center gap-1.5 text-xs text-green-400 font-medium ml-2">
                                <span className="flex h-2 w-2 rounded-full bg-green-400 animate-pulse" />
                                {liveCount} live
                            </span>
                        )}
                    </div>
                    <p className="text-muted-foreground text-sm">
                        {isHistoricalSeason
                            ? `Results, table and top scorers for ${activeSeason?.name || 'this season'}`
                            : 'News, results, tables and top scorers'}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
                        <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                    </Button>
                    <Link to="/football">
                        <Button size="sm" className="gap-1.5">
                            <Target className="h-4 w-4" />
                            Predict
                            <ArrowRight className="h-3.5 w-3.5" />
                        </Button>
                    </Link>
                </div>
            </div>

            {/* My Teams — not relevant while browsing a past season */}
            {!isHistoricalSeason && <MyTeamsSection navigate={navigate} />}

            {/* Fixtures Carousel */}
            <section>
                {/* Clickable heading — opens team picker */}
                <button
                    className="flex items-center gap-2 mb-3 group w-full text-left"
                    onClick={() => setTeamPickerOpen(true)}
                >
                    <div className="w-1 h-5 bg-primary rounded-full flex-shrink-0" />
                    <h2 className="font-bold text-lg group-hover:text-primary transition-colors">
                        Fixtures & Results
                    </h2>
                    <span className="text-xs text-muted-foreground group-hover:text-primary transition-colors">▾</span>
                </button>

                {resultsView === 'carousel' ? (
                    <>
                        {/* Active filter chip */}
                        {filteredTeam && (
                            <div className="flex items-center gap-2 mb-3">
                                <div className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full border border-primary/30 bg-primary/10">
                                    <img src={badgeUrl(filteredTeam.id)} alt={filteredTeam.name}
                                        className="w-4 h-4 object-contain flex-shrink-0"
                                        onError={e => { e.target.style.display = 'none'; }} />
                                    <span className="text-xs font-semibold text-primary/90">{filteredTeam.name}</span>
                                    <button
                                        onClick={() => setFilteredTeam(null)}
                                        className="text-primary/50 hover:text-primary ml-0.5 text-xs leading-none transition-colors"
                                        aria-label="Clear filter"
                                    >✕</button>
                                </div>
                                <span className="text-xs text-muted-foreground">
                                    {fixtures.filter(m => String(m.home_team_id) === String(filteredTeam.id) || String(m.away_team_id) === String(filteredTeam.id)).length} matches
                                </span>
                            </div>
                        )}

                        <FixturesCarousel
                            matches={filteredTeam
                                ? fixtures.filter(m => String(m.home_team_id) === String(filteredTeam.id) || String(m.away_team_id) === String(filteredTeam.id))
                                : fixtures}
                            loading={fixturesLoading}
                        />
                    </>
                ) : (
                    <>
                        <div className="flex items-center gap-2 mb-3">
                            <Badge variant="outline" className="text-xs font-semibold">
                                {resultsView === 'results' || resultsView === 'results-list' ? 'Results' : 'Fixtures'} for {compName}
                            </Badge>
                            <button
                                onClick={() => setResultsView('carousel')}
                                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                            >
                                ✕ Back to fixtures view
                            </button>
                        </div>
                        <MatchListView
                            matches={fullSeasonMatches}
                            mode={resultsView === 'results-list' ? 'results' : 'fixtures'}
                            loading={fullSeasonLoading}
                        />
                    </>
                )}
            </section>

            {/* Team picker dialog */}
            {teamPickerOpen && (() => {
                // Filter out knockout-stage placeholder names
                const isPlaceholder = (name) =>
                    !name ||
                    /^(Winner|Loser|Runner-up|Group [A-Z]\/)/i.test(name) ||
                    /^(Winner|Loser|Runner-up)\s+(Group|Match)/i.test(name) ||
                    /^(Group [A-Z])\s*$/i.test(name);

                // Build unique real-team list from current fixtures
                const seen = new Set();
                const teams = [];
                fixtures.forEach(m => {
                    if (m.home_team_id && !seen.has(m.home_team_id) && !isPlaceholder(m.home_team_name)) {
                        seen.add(m.home_team_id);
                        teams.push({ id: m.home_team_id, name: m.home_team_name });
                    }
                    if (m.away_team_id && !seen.has(m.away_team_id) && !isPlaceholder(m.away_team_name)) {
                        seen.add(m.away_team_id);
                        teams.push({ id: m.away_team_id, name: m.away_team_name });
                    }
                });
                teams.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

                return (
                    <div
                        className="fixed inset-0 z-50 flex flex-col"
                        style={{ background: 'rgba(3,7,18,0.97)', backdropFilter: 'blur(12px)' }}
                    >
                        {/* Ambient glows */}
                        <div className="absolute inset-0 pointer-events-none overflow-hidden">
                            <div className="absolute -top-20 left-1/4 w-[500px] h-[500px] rounded-full blur-[120px]"
                                style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.15), transparent 70%)' }} />
                            <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full blur-[100px]"
                                style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.12), transparent 70%)' }} />
                        </div>

                        {/* Header */}
                        <div className="relative z-10 flex items-center justify-between px-6 pt-5 pb-4 flex-shrink-0">
                            <div>
                                <p className="text-white/35 text-[10px] font-bold uppercase tracking-[0.25em] mb-0.5">
                                    Fixtures & Results
                                </p>
                                <h2 className="text-white text-xl font-black tracking-tight">Filter by Team</h2>
                            </div>
                            <div className="flex items-center gap-3">
                                {filteredTeam && (
                                    <button
                                        onClick={() => { setFilteredTeam(null); setTeamPickerOpen(false); }}
                                        className="text-xs font-semibold text-white/50 hover:text-white border border-white/15 hover:border-white/40 px-4 py-2 rounded-full transition-all"
                                    >
                                        Clear filter
                                    </button>
                                )}
                                <button
                                    onClick={() => setTeamPickerOpen(false)}
                                    className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/50 hover:text-white hover:border-white/40 transition-all"
                                >✕</button>
                            </div>
                        </div>

                        {/* Whole-league quick actions — bypass the team filter entirely */}
                        <div className="relative z-10 flex items-center gap-2 px-6 pb-4 flex-shrink-0">
                            <button
                                onClick={() => { setResultsView('fixtures-list'); setFilteredTeam(null); setTeamPickerOpen(false); }}
                                className="flex items-center gap-1.5 text-xs font-semibold text-white/70 hover:text-white border border-white/15 hover:border-white/40 px-3.5 py-1.5 rounded-full transition-all"
                            >
                                <Calendar className="h-3.5 w-3.5" />
                                Fixtures for League
                            </button>
                            <button
                                onClick={() => { setResultsView('results-list'); setFilteredTeam(null); setTeamPickerOpen(false); }}
                                className="flex items-center gap-1.5 text-xs font-semibold text-white/70 hover:text-white border border-white/15 hover:border-white/40 px-3.5 py-1.5 rounded-full transition-all"
                            >
                                <Trophy className="h-3.5 w-3.5" />
                                Results for League
                            </button>
                        </div>

                        {/* Badge grid — responsive: scrollable 4-col on mobile, fill-viewport on desktop */}
                        {(() => {
                            const isMobile = window.innerWidth < 640;
                            const n = teams.length;

                            // Desktop: calculate cols to fill the screen landscape
                            let desktopCols = 12;
                            if (!isMobile) {
                                let best = 12, bestDiff = Infinity;
                                for (let c = 6; c <= 16; c++) {
                                    const r = Math.ceil(n / c);
                                    const diff = Math.abs(c / r - 3);
                                    if (diff < bestDiff) { bestDiff = diff; best = c; }
                                }
                                desktopCols = best;
                            }

                            const cols = isMobile ? 4 : desktopCols;
                            const rows = Math.ceil(n / cols);

                            // Mobile: scroll with fixed tile size; Desktop: aim to fill the
                            // remaining viewport height, but scroll rather than clip when a
                            // league (e.g. a 36-team Champions League phase) needs more room
                            // than that — a grid row can't shrink below its tile content's
                            // natural size, so "fits exactly" is a target, not a guarantee.
                            const containerStyle = isMobile
                                ? { flex: '1 1 0', minHeight: 0, overflowY: 'auto', padding: '0 12px 16px' }
                                : { flex: '1 1 0', minHeight: 0, overflowY: 'auto', padding: '0 16px 16px' };

                            const gridStyle = isMobile
                                ? {
                                    display: 'grid',
                                    gridTemplateColumns: `repeat(${cols}, 1fr)`,
                                    gap: '8px',
                                }
                                : {
                                    display: 'grid', width: '100%', minHeight: '100%',
                                    gridTemplateColumns: `repeat(${cols}, 1fr)`,
                                    gridTemplateRows: `repeat(${rows}, 1fr)`,
                                    gap: '6px',
                                };

                            return (
                                <div className="relative z-10" style={containerStyle}>
                                    <div style={gridStyle}>
                                        {teams.map(team => {
                                            const isSelected = filteredTeam?.id === team.id;
                                            return (
                                                <button
                                                    key={team.id}
                                                    onClick={() => { setFilteredTeam(team); setResultsView('carousel'); setTeamPickerOpen(false); }}
                                                    className="relative flex flex-col items-center justify-center rounded-2xl transition-all duration-200 active:scale-95 group"
                                                    style={{
                                                        // Mobile: fixed comfortable height so badges are large enough to tap
                                                        ...(isMobile ? { height: 90, padding: '10px 6px 8px' } : {}),
                                                        background: isSelected
                                                            ? 'linear-gradient(135deg, rgba(6,182,212,0.20), rgba(139,92,246,0.14))'
                                                            : 'rgba(255,255,255,0.04)',
                                                        border: isSelected
                                                            ? '1.5px solid rgba(6,182,212,0.65)'
                                                            : '1.5px solid rgba(255,255,255,0.07)',
                                                        boxShadow: isSelected ? '0 0 28px rgba(6,182,212,0.25)' : 'none',
                                                    }}
                                                >
                                                    <div
                                                        className="flex items-center justify-center transition-transform duration-200 active:scale-110"
                                                        style={{
                                                            width: isMobile ? 44 : '100%',
                                                            height: isMobile ? 44 : '55%',
                                                            flexShrink: 0,
                                                            filter: isSelected ? 'drop-shadow(0 0 8px rgba(6,182,212,0.55))' : 'none',
                                                        }}
                                                    >
                                                        <img
                                                            src={badgeUrl(team.id)}
                                                            alt={team.name}
                                                            className="w-full h-full object-contain"
                                                            onError={e => { e.target.style.display = 'none'; }}
                                                        />
                                                    </div>
                                                    <span
                                                        className="text-center font-semibold leading-tight line-clamp-2 mt-1 px-1"
                                                        style={{
                                                            fontSize: isMobile ? 9 : 'clamp(8px, 1vw, 11px)',
                                                            color: isSelected ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.6)',
                                                            maxWidth: '100%',
                                                        }}
                                                    >
                                                        {team.name}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                );
            })()}

            {/* News */}
            <section>
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-1 h-5 bg-primary rounded-full" />
                    <h2 className="font-bold text-lg">Latest News</h2>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground ml-1">
                        <span>powered by</span>
                        <img
                            src="https://explore.pressassociation.com/images/press-association-explore-logo.svg"
                            alt="Press Association"
                            className="h-3.5 w-auto opacity-60 dark:invert"
                        />
                    </div>
                </div>
                <NewsSection items={news} loading={newsLoading} onArticleClick={setArticleId} />
            </section>

            {showBrackets ? (
                /* Brackets on: full-width bracket, then Top Scorers below */
                <>
                    <section>
                        <div className="flex items-center gap-2 mb-4">
                            <div className="w-1 h-5 bg-amber-400 rounded-full" />
                            <h2 className="font-bold text-lg">Knockout Bracket</h2>
                        </div>
                        <TournamentBracket rounds={bracket} loading={bracketLoading} />
                    </section>
                    <section>
                        <div className="flex items-center gap-2 mb-4">
                            <div className="w-1 h-5 bg-amber-400 rounded-full" />
                            <h2 className="font-bold text-lg">Top Scorers</h2>
                        </div>
                        <TopScorersSection scorers={scorers} loading={scorersLoading} onPlayerClick={setProfilePlayer} groups={groups} isWc={Number(hubCompId) === 700} />
                    </section>
                </>
            ) : (
                /* Table + Scorers — equal-height columns */
                <div className="grid lg:grid-cols-2 gap-6 items-stretch">
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="w-1 h-5 bg-amber-400 rounded-full" />
                            <h2 className="font-bold text-lg">Table</h2>
                        </div>
                        <div className="flex-1">
                            <TableSection groups={groups} loading={tableLoading} />
                        </div>
                    </div>
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="w-1 h-5 bg-amber-400 rounded-full" />
                            <h2 className="font-bold text-lg">Top Scorers</h2>
                        </div>
                        <div className="flex-1">
                            <TopScorersSection scorers={scorers} loading={scorersLoading} onPlayerClick={setProfilePlayer} groups={groups} isWc={Number(hubCompId) === 700} />
                        </div>
                    </div>
                </div>
            )}

            {/* CTA */}
            <Card className="border-primary/30 bg-gradient-to-r from-primary/10 to-transparent">
                <CardContent className="py-8 flex flex-col sm:flex-row items-center justify-between gap-4 px-6">
                    <div>
                        <h3 className="text-xl font-bold font-heading mb-1">Think you know the scores?</h3>
                        <p className="text-muted-foreground text-sm">Join a prediction competition and prove it — predict scores, earn points, climb the leaderboard.</p>
                    </div>
                    <Link to="/football" className="flex-shrink-0">
                        <Button size="lg" className="gap-2 font-semibold">
                            <Trophy className="h-4 w-4" />
                            Join a Competition
                            <ArrowRight className="h-4 w-4" />
                        </Button>
                    </Link>
                </CardContent>
            </Card>

            {/* Article modal */}
            {articleId && <ArticleModal articleId={articleId} onClose={() => setArticleId(null)} />}

            {/* Player profile modal */}
            {profilePlayer && (
                <PlayerProfileModal
                    playerId={profilePlayer.player_id}
                    playerName={profilePlayer.player}
                    teamId={profilePlayer.team_id}
                    onClose={() => setProfilePlayer(null)}
                />
            )}
        </div>
    );
}
